// Core attributes and parameter names have one owner; only dirty values enter PATCH.
export const coreSections = [
  { title: 'Profile', fields: [
    ['name_full', 'Full name for invoices'], ['slug', 'URL identifier'], ['iso_code', 'Country code'],
    ['city', 'City'], ['address', 'Address'], ['registered_address', 'Registered address'],
    ['vat_number', 'VAT number'], ['website', 'Website'], ['tier', 'Partner tier', 'tier'], ['is_agency', 'Is agency', 'boolean'],
  ] },
  { title: 'Contacts', fields: [
    ['contact_person', 'Contact person'], ['phone', 'Phone'], ['email', 'Email'], ['login', 'Login'],
    ['language', 'Primary language'], ['financial_language', 'Finance language'], ['financial_email', 'Finance email'],
    ['partnership_manager_id', 'Partnership manager', 'employee'], ['financial_controller_id', 'Financial controller', 'employee'],
  ] },
  { title: 'Operations', fields: [
    ['timezone', 'Timezone'],
    ['speed_answer', 'Response speed', 'number'], ['is_required_hospital_number', 'Require hospital patient number', 'boolean', 'The partner needs its internal patient ID to match the case and process payment. CRM and the partner account request it when confirming a clinic or appointment.'],
    ['specified_payment_pay', 'Payment by specified date', 'number'], ['publication_notify_bookimed', 'Notify Bookimed of publications', 'boolean'],
    ['price_link', 'Price list URL'], ['link_to_google_docs', 'Google Docs URL'],
    ['comment', 'Comment', 'textarea'], ['special_questionnaire', 'Special questionnaire', 'textarea'],
  ] },
];
export const scheduleScalarNames = ['is_around_the_clock'];
// Values match the partner tier choices in the old admin; the stored value is unchanged.
export const partnerTiers = ['TOP', 'High potential', 'Low priority', 'New', 'Launch'];
export const parameterSections = [
  { title: 'Partner settings', fields: [
    ['works_in_chat', 'Works in chat', 'boolean'], ['autopublication_self_service', 'Self-service autopublication', 'boolean'],
    ['is_new', 'New partner', 'boolean'], ['kids_only', 'Children only', 'boolean'], ['send_to_all_emails', 'Send to all emails', 'boolean'],
    ['dont_work_in_lk', 'Does not work in partner account', 'boolean'], ['top', 'Top partner', 'boolean'],
    ['booki_help', 'Bookimed support', 'boolean'], ['insurance', 'Insurance', 'boolean'], ['is_marketplace', 'Marketplace', 'boolean'],
    ['has_stack_out', 'Stack out', 'boolean'], ['rating', 'Rating', 'decimal'], ['skype', 'Skype'], ['other_messengers', 'Other messengers'],
    ['sla_from', 'SLA from'], ['sla_to', 'SLA to'], ['sla_comment', 'SLA comment', 'textarea'],
  ] },
  { title: 'Financial terms', fields: [
    ['contract_number', 'Contract number'], ['contract_expires', 'Contract expires', 'date'],
    ['report_deadline_day', 'Report deadline day', 'number'], ['invoice_deadline_delay', 'Invoice deadline delay', 'number'],
    ['skip_finance_check_date_to', 'Skip finance check until', 'date'], ['deferment_of_payment', 'Payment deferral', 'number'], ['is_working_days', 'Working days', 'boolean'],
    ['deposit_bookimed', 'Bookimed deposit', 'boolean'], ['deposit', 'Own deposit', 'boolean'], ['deposit_comment', 'Deposit comment', 'textarea'],
  ] },
];
export const contactFields = [
  ['head_contact', 'Head contact', ['name', 'role', 'email', 'phone']],
  ['financial_contact', 'Financial contact', ['name', 'phone']],
];
export const copy = value => JSON.parse(JSON.stringify(value));
export const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
export const booleanValue = value => value === true || value === 1 || value === '1' || value === 'true';
export function normalizeScheduleTime(value) {
  if (typeof value !== 'string') return value;
  const match = /^(\d{1,2})(?::([0-5]\d))?$/.exec(value);
  if (!match || Number(match[1]) > 23) return value;
  return `${match[1].padStart(2, '0')}:${match[2] || '00'}`;
}
export function normalizeEditorAgency(value) {
  const agency = copy(value);
  if (agency.timezone && typeof agency.timezone === 'object') agency.timezone = agency.timezone.timezone;
  agency.schedule = Object.values(agency.schedule || {}).map(day => ({ ...day, work_time_from: normalizeScheduleTime(day.work_time_from), work_time_to: normalizeScheduleTime(day.work_time_to), is_weekend: booleanValue(day.is_weekend) }));
  return agency;
}
export function requireAgencyResult(data) {
  if (!data || data.status === false || data.error) {
    const error = new Error(data?.error?.data?.detail || data?.error?.detail || data?.error?.code || 'Request failed');
    error.responseData = data;
    throw error;
  }
  if (!data.agency || !data.agency.id || !data.agency_settings || !Object.prototype.hasOwnProperty.call(data.agency_settings, 'settings')) throw new Error('Agency response is incomplete');
  return data;
}
export function buildPatch(original, current, settingsBefore, settingsAfter) {
  const patch = {};
  const scalarNames = [...scheduleScalarNames, ...coreSections.flatMap(section => section.fields.map(field => field[0]))];
  scalarNames.forEach(name => { if (!same(original[name], current[name])) patch[name] = current[name]; });
  const translations = {};
  Object.keys(current.name).forEach(locale => {
    if (!same(original.name[locale], current.name[locale])) translations[locale] = current.name[locale];
  });
  if (Object.keys(translations).length) patch.name = translations;
  if (!same(original.schedule, current.schedule)) patch.schedule = current.schedule;
  const settings = {};
  Object.keys(settingsAfter).forEach(name => { if (!same(settingsBefore[name], settingsAfter[name])) settings[name] = settingsAfter[name]; });
  if (Object.keys(settings).length) patch.settings = settings;
  return patch;
}
